import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Fragment, useEffect, useCallback } from "react";
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import Tab, { TabProps } from "../../../molecules/Tab";
import PageForm from "../../../templates/PageForm";
import { computed, useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import useFetch from "../../../../hooks/useFetch";
import { TbLetterV, TbUser, TbShield, TbHeart, TbPencil, TbCreditCard, TbGift, TbReceipt, TbMapPin, TbPhone, TbFileText } from "react-icons/tb";
import { storage } from "../../../../utils/storage";
import { removeErrorField } from "../../../../utils/formErrorUtils";
import IndividualFormBasicData from "./IndividualFormBasicData";
import IndividualFormAdditionalData from "./IndividualFormAdditionalData";
import IndividualFormSubscription from "./IndividualFormSubscription";
import IndividualFormConsentingParty from "./IndividualFormConsentingParty";
import IndividualFormBankAccount from "./IndividualFormBankAccount";
import IndividualFormAddresses from "./IndividualFormAddresses";
import IndividualFormContacts from "./IndividualFormContacts";
import IndividualFormDocuments from "./IndividualFormDocuments";
import useCities from "../../../../hooks/useCities";
import useProvinces from "../../../../hooks/useProvinces";
import { useProfileLoader } from "../../../../hooks/useProfileLoader";
import { ProfileStorageManager } from "../../../../utils/profileStorageManager";



const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/individual`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const profileTabList = [
    {
        name: 'CONSENTING_PARTY-ISSUER-GUARANTOR',
        id: 'tab-1',
        title: 'Dados complementares',
        index: 1,
        icon: TbUser,  // 👤 Dados pessoais
    },
    {
        name: 'CONSENTING_PARTY-GUARANTOR-ISSUER-SIGNATORY',
        id: 'tab-2',
        title: 'Assinatura',
        index: 2,
        icon: TbPencil,  // ✏️ Assinatura
    },
    {
        name: 'GUARANTOR-ISSUER-SIGNATORY',
        id: 'tab-3',
        title: 'Anuentes',
        index: 3,
        icon: TbShield,  // 🛡️ Avalista/Garantidor
    },
    {
        name: 'ALL',
        id: 'tab-4',
        title: 'Endereços',
        index: 4,
        icon: TbMapPin,  // 📍 Endereços
    },
    {
        name: 'ALL',
        id: 'tab-5',
        title: 'Contatos',
        index: 5,
        icon: TbPhone,  // 📞 Contatos
    },
    {
        name: 'ALL',
        id: 'tab-6',
        title: 'Documentos',
        index: 6,
        icon: TbFileText,  // 📄 Documentos
    },
    {
        name: 'FINANCIAL_REPRESENTATIVE',
        id: 'tab-7',
        title: 'Contas bancárias',
        index: 7,
        icon: TbCreditCard,  // 💳 Financeiro
    }
]

type Address = {
    person_id: string;
    id: string;
    zipcode: string;
    street: string;
    street_number: string;
    complement: string;
    district: string;
    city_id: string;
    street_type: string;
    is_active: boolean;
    province_initials: string;
}

type Form = {
    id: string;
    uuid: string;
    socialId: string;
    name: string;
    birthday: string;
    maritalStatus: string;
    nationalityId: string;
    professionId: string;
    matrimonialRegimeId: string;
    spouseSocialId: string;
    spouseName?: string;
    gender: string;
    isCountryman: string;
    PEP: string;
    activeAddress: Address;
    idCardType: string;
    idCard: string;
    idCardProvince: string;
    issuingAuthority: string;
    email: string;
    phone: string;
    mobile: string;
    isNationalNumber: string;
    addresses: any[];
    profiles: any[];
    contacts: any[];
    documents?: any[];
    selectedSubscriptionType: any[];
    province: string;
    banks: any[];
    bankAccounts: any[];
    consentingParty: {
        uuid: string;
        name: string;
        socialId: string;
    }
    selectedConsentingParty?: string;
    blockEdit: {
        blockIdCard: boolean;
        blockBirthday: boolean;
        blockNationality: boolean;
    }
}

type DataRow = {
    idx: number;
    profile: string;
    label: string;
    signAs: string;
    useDigitalCertificate: boolean;
    canDelete: boolean;
}

type ConsentingParty = {
    value: string;
    label: string;
    name: string;
    socialId: string;
}

const IndividualPageEdit = () => {
    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();
    const banks = useSignal<any[]>([]);
    const { personId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isSendingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const isLoadingForm = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const genders = useSignal<any[]>([
        { value: '0', label: 'Feminino' },
        { value: '1', label: 'Masculino' }
    ]);
    const subscriptionTypes = useSignal<any[]>([]);
    const individualUUID = useSignal<string>('');
    const isEditing = useSignal<boolean>(false);
    const profilesMap: { [key: string]: string } = {
        "ISSUER": "Emitente",
        "RECIPIENT": "Beneficiário",
        "GUARANTOR-RETAIL": "Avalista Varejo",
        "GUARANTOR-WHOLESALE": "Avalista Atacado",
        "SIGNATORY": "Signatário",
        "GUARANTOR": "Avalista",
        "CONSENTING_PARTY": "Anuente",
        "FINANCIAL_REPRESENTATIVE": "Representante Financeiro",
    };

    const form = useSignal<Form>({
        id: '',
        uuid: '',
        socialId: '',
        name: '',
        birthday: '',
        maritalStatus: '',
        nationalityId: '',
        professionId: '',
        matrimonialRegimeId: '',
        spouseSocialId: '',
        spouseName: '',
        gender: '',
        isCountryman: '',
        PEP: '',
        province: '',
        email: '',
        phone: '',
        mobile: '',
        isNationalNumber: "true",
        idCardType: '',
        idCard: '',
        idCardProvince: '',
        issuingAuthority: '',
        activeAddress: {
            person_id: '',
            id: '',
            zipcode: '',
            street: '',
            street_number: '',
            complement: '',
            district: '',
            city_id: '',
            street_type: '',
            is_active: false,
            province_initials: '',
        },
        addresses: [],
        profiles: [],
        contacts: [],
        selectedSubscriptionType: [],
        banks: [],
        bankAccounts: [],
        consentingParty: {
            uuid: '',
            name: '',
            socialId: '',
        },
        selectedConsentingParty: '',
        blockEdit: {
            blockIdCard: false,
            blockBirthday: false,
            blockNationality: false,
        }
    });


    const tabItemsList: TabProps[] = [
        { id: "tab-0", title: 'Dados básicos', index: 0 },
        { id: "tab-1", title: 'Dados Complementares', index: 1 },
        { id: "tab-2", title: 'Assinaturas', index: 2 },
        { id: "tab-3", title: 'Anuentes', index: 3 },
        { id: "tab-4", title: 'Endereços', index: 4 },
        { id: "tab-5", title: 'Contatos', index: 5 },
        { id: "tab-6", title: 'Documentos', index: 6 },
        { id: "tab-7", title: 'Contas bancárias', index: 7 },
    ];
    const tabItems = useSignal<TabProps[]>([]);
    const [searchParams] = useSearchParams();
    const tabIndex: number = parseInt(searchParams.get('tab') || "0");
    const profiles = searchParams.get('profiles') || '';  // ✅ Manter para compatibilidade
    
    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${personId}/page-form`, [{ key: 'x-api-key', value: API_KEY }]);

    // 🔧 CORREÇÃO: SEMPRE usar personId (UUID estável) para localStorage
    const getPersonUUID = useCallback(() => {
        return personId; // Chave estável, sempre UUID correto
    }, [personId]);

    // ✅ CORREÇÃO: Memoizar a função loadFromDatabase para evitar loop infinito
    const loadFromDatabase = useCallback(async () => {
        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());
            
            const response = await fetch(`${BASE_URL}/${personId}`, {
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`,
                }
            });
            if (response.ok) {
                const data = await response.json();
                return data.profiles?.map((p: any) => p.type) || [];
            }
        } catch (error) {
            console.warn('Erro ao carregar profiles do banco:', error);
        }
        return [];
    }, [personId]); // ✅ Dependência estável

    // ✅ CORREÇÃO: SEMPRE usar personId (UUID) - NUNCA individualUUID.value
    const personUUID = personId || ''; // Fallback para string vazia
    const { profiles: loadedProfiles, saveProfiles, clearProfiles } = useProfileLoader(
        'individual', 
        personUUID,
        loadFromDatabase // ✅ Função memoizada, não recriada a cada render
    );
    
    const profilesArray = useSignal<string[]>(loadedProfiles || []);
    const profilesSubscription = useSignal<DataRow[]>([]);

    const getTabItem = (id: string): TabProps => {
        const tab = tabItemsList.find(item => item.id === id);
        if (!tab) {
            throw new Error(`Tab with id "${id}" not found`);
        }
        return tab;
    }

    const addTab = (tab: TabProps) => {
        if (!tabItems.value.find(item => item.id === tab.id)) {
            tabItems.value.push(tab);
        }
        tabItems.value.sort((a, b) => a.index - b.index);
    }

    const loadDefaultSubscriptionTypes = (file: any) => {
        let values: DataRow[] = [];
        //carrega os perfis de assinatura que ja estao cadastrados
        //este codigo e para carregar os tipos de asinatura que estao na base de dados, o codigo seguinte preenche os tipos de asinatura a partir dos perfis selecionados
        if (file.profiles && file.profiles.length > 0) {
            file.profiles.forEach((profile: any) => {
                let sign_as = profile.sign_as;
                if (sign_as && sign_as.length > 0) {
                    sign_as.forEach((record: any) => {
                        if (!profileSubscriptionContains(values, record.sign_as)) {
                            let item = {
                                idx: record.id,
                                profile: profile.type,
                                label: profilesMap[profile.type] || '',
                                useDigitalCertificate: record.use_digital_certificate || false,
                                canDelete: true,
                                signAs: record.sign_as || ''
                            };
                            values.push(item);
                        }
                    });
                }
            });
        }

        let tmp = ["CONSENTING_PARTY"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "CONSENTING_PARTY");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }
        tmp = ["GUARANTOR"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "GUARANTOR");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }
        tmp = ["ISSUER"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "ISSUER");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }


        form.value = {
            ...form.value,
            selectedSubscriptionType: values,
        }
    }

    const loadProfilesSubscription = async () => {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${BASE_URL}` + "/subscription-profiles", {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY || '',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }
        });

        if (!response.ok) {
            toast.error("Erro ao carregar os perfis de assinatura.");
            return;
        }
        const responseData = await response.json();
        let profilesSubscription = responseData.Profiles || [];
        let idx = 0;
        let itens: any[] = [];
        let profiles = profilesArray.value;

        profilesSubscription.forEach((element: any) => {
            if (profiles.includes(element.profile)) {
                element.values.forEach((value: any) => {
                    let item = {
                        profile: element.profile,
                        label: element.label + "-" + value.sign_as,
                        labelProfile: element.label,
                        sign_as: value.sign_as,
                        value: idx++,
                    }
                    itens.push(item);
                });
            }
        });
        subscriptionTypes.value = itens;
        loadDefaultSubscriptionTypes(data);
    }

    const validarCPF = (socialId: string) => {
        // Remove caracteres não numéricos
        const cpfLimpo = socialId.replace(/[^\d]/g, '');
        // Verifica se o CPF tem 11 dígitos e se não são todos iguais (ex: "111.111.111-11")
        if (cpfLimpo.length !== 11 || /^(\d)\1{10}$/.test(cpfLimpo)) {
            return false;
        }
        let soma: number;
        let resto: number;
        // Validação do primeiro dígito verificador
        soma = 0;
        for (let i = 0; i < 9; i++) {
            soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
        }
        resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) {
            resto = 0;
        }
        if (resto !== parseInt(cpfLimpo.charAt(9))) {
            return false;
        }
        // Validação do segundo dígito verificador
        soma = 0;
        for (let i = 0; i < 10; i++) {
            soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
        }
        resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) {
            resto = 0;
        }
        if (resto !== parseInt(cpfLimpo.charAt(10))) {
            return false;
        }
        return true;
    }


    useEffect(() => {
        formatMobilePhone();
    }, [form.value.isNationalNumber])

    // ✅ Sincronizar profiles carregados com profilesArray E forçar reload
    useEffect(() => {
        if (loadedProfiles.length > 0 && JSON.stringify(loadedProfiles) !== JSON.stringify(profilesArray.value)) {
            profilesArray.value = loadedProfiles;
            console.log(`🔄 Profiles sincronizados: ${loadedProfiles.join(', ')}`);
            
            // ✅ FORÇAR atualização das abas quando profiles mudam
            profilesArray.value.forEach((profile: any) => {
                profileTabList.forEach(profileTab => {
                    if (profileTab.name.includes(profile)) {
                        const tabItem = profileTab;
                        addTab(getTabItem(tabItem.id));
                    }
                });
            });
        }
    }, [loadedProfiles]);

    // ✅ CORREÇÃO: Evitar conflito entre loadedProfiles e individualUUID
    useEffect(() => {
        // Só executar se NÃO há profiles já carregados via loadedProfiles
        if (individualUUID.value && 
            individualUUID.value !== 'temp-individual-id' && 
            loadedProfiles.length === 0) { // ← CONDIÇÃO CRÍTICA
            
            console.log('🔄 Carregando profiles para individualUUID.value:', individualUUID.value);
        }
    }, [individualUUID.value, loadedProfiles.length]); // ← Dependência dupla

    const loadProvinces = async () => {
        isLoadingForm.value = true;
        try {
            provinces.value = await fetchProvinces();
        } catch (error) {
            toast.error("Erro ao carregar os estados.");
        }
        isLoadingForm.value = false;
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const updateButtonDisable = computed(() => {
        let disabled = false;
        let prf = profilesArray.value;
        let tmp = ["ISSUER"];
        if (tmp.some((profile: string) => prf.includes(profile))) {
            if (form.value.birthday === '' || form.value.nationalityId === '' || form.value.maritalStatus === '')
                disabled = true;
        }

        /*
        Se perfil Avalista selecionado, obrigatório:
        ->Nascimento
        ->Nacionalidade
        ->Profissão
        ->Estado civil
                Se estado civil = união estável ou casado -> obrigatório Regime de casamento
                    Se regime de casamento = comunhão universal ou parcial -> obrigatório Cônjuge
        */
        tmp = ["GUARANTOR"];
        if (tmp.some((profile: string) => prf.includes(profile))) {
            if (form.value.birthday === '' || form.value.nationalityId === '' || form.value.professionId === '' || form.value.maritalStatus === '')
                disabled = true;
            if (form.value.maritalStatus === '2' || form.value.maritalStatus === '6') {
                if (form.value.matrimonialRegimeId === '')
                    disabled = true;
                if (form.value.matrimonialRegimeId === "1" || form.value.matrimonialRegimeId === "2") {
                    if (form.value.spouseSocialId === '') {
                        disabled = true;
                    }
                }
            }
        }

        tmp = ["CONSENTING_PARTY"];
        if (tmp.some((profile: string) => prf.includes(profile))) {
            if (form.value.birthday === '')
                disabled = true;
        }

        tmp = ["RECIPIENT"];
        if (tmp.some((profile: string) => prf.includes(profile))) {
            if (form.value.birthday === '' || form.value.nationalityId === '')
                disabled = true;
        }

        return disabled;
    });

    const getCity = async (cityId: number) => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${process.env.REACT_APP_GEOLOCATION_API_URL}/cities/${cityId}?fields=id,name,province.initials`, {
                headers: {
                    "x-api-key": process.env.REACT_APP_API_KEY || "",
                },
            });
            isLoadingForm.value = false;
            return await response.json();
        } catch (error) {
            toast.error("Erro ao carregar a cidade. Tente novamente!");
        }
        isLoadingForm.value = false;
    };

    const cancelForm = () => {
        // 🧹 CORREÇÃO: Limpar localStorage ao cancelar
        const personUUID = getPersonUUID() || "";
        ProfileStorageManager.clear('individual', personUUID);
        console.log('🗑️ Cancelamento - localStorage limpo para:', personUUID);
        
        // Usar estado persistido ou fallback para padrão DESC
        const listState = storage.getIndividualListState();
        const { page, pageSize, field, order } = listState;
        
        navigate(`/individual/individual-list/${page}/${pageSize}/${field}/${order}/`);
    };

    const submitForm = async () => {
        if (form.value.spouseSocialId != "" && !validarCPF(form.value.spouseSocialId)) {
            toast.error("CPF do cônjuge inválido. Por favor, verifique o CPF informado.");
            return false;
        }
        if (!verifySelectedSubscriptionType()) {
            return false;
        } else {
            isSendingForm.value = true;
            try {
                form.value.contacts.forEach((contact: any) => {
                    if (contact.type == 'MOBILE') {
                        contact.is_national_number = form.value.isNationalNumber == "true" ? true : false;
                        contact.content = form.value.mobile.replace(/\D/g, '');
                    }
                    if (contact.type == 'PHONE') {
                        contact.content = form.value.phone.replace(/\D/g, '');
                    }
                    if (contact.type == 'EMAIL') {
                        contact.content = form.value.email;
                    }
                });

                // ✅ SIMPLIFICADO: usar person.uuid como fonte da verdade
                let finalProfiles = profilesArray.value;
                
                console.log('🔍 DEBUG update function:');
                console.log('  - profilesArray.value inicial:', profilesArray.value);
                
                // Garantir que localStorage seja a fonte da verdade
                try {
                    const personUUID = getPersonUUID() || "";
                    const storedProfiles = ProfileStorageManager.load('individual', personUUID);
                    console.log('  - storedProfiles do localStorage:', storedProfiles);
                    if (storedProfiles.length > 0) {
                        finalProfiles = storedProfiles;
                        console.log(`💾 Usando profiles do localStorage: ${finalProfiles.join(', ')}`);
                    }
                } catch (error) {
                    console.warn('⚠️ Erro ao ler localStorage no update, usando profilesArray:', error);
                }

                console.log('  - finalProfiles que será enviado:', finalProfiles);

                form.value = {
                    ...form.value,
                    profiles: finalProfiles,
                }
                var dataToSubmit = form.value;
                dataToSubmit.mobile = dataToSubmit.mobile.replace(/\D/g, '');
                dataToSubmit.phone = dataToSubmit.phone.replace(/\D/g, '');

                // Não enviar activeAddress se zipcode estiver vazio
                if (!dataToSubmit.activeAddress?.zipcode || dataToSubmit.activeAddress.zipcode.trim() === '') {
                    const { activeAddress, ...rest } = dataToSubmit;
                    dataToSubmit = rest as typeof dataToSubmit;
                }

                if (dataToSubmit.idCardType === "RNE") {
                    dataToSubmit.idCardProvince = "";
                    dataToSubmit.issuingAuthority = "";
                }

                const token = localStorage.getItem('authToken');
                const response = await fetch(`${BASE_URL}` + "/edit", {
                    method: 'POST',
                    headers: {
                        'x-api-key': API_KEY || '',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(dataToSubmit),
                });
                if (response.status === 401) {
                    navigate("/denied");
                    return;
                }
                if (response.ok) {
                    // ✅ Limpar localStorage após sucesso
                    const personUUID = getPersonUUID() || "";
                    ProfileStorageManager.clear('individual', personUUID);
                    console.log('🗑️ Profiles salvos com sucesso, localStorage limpo');
                    
                    toast.success('Dados gravados com sucesso!');
                    navigate(-1);
                } else {
                    const { errors, message } = await response.json();
                    errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
                    if (errorFields.value.length > 0) {
                        errorFields.value.forEach((errorField: ErrorField) => {
                            toast.error(errorField.message);
                        });
                    }
                }
            } catch (error) {
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
            isSendingForm.value = false;
        }
    };

    const verifySelectedSubscriptionType = () => {
        // de acordo com a especificação, é necessário ter pelo menos um tipo de assinatura para cada tipo de perfil selecionado
        let selectedProfiles = profilesArray.value;
        let selectedSubscriptionTypes = form.value.selectedSubscriptionType;
        let count_not_found = 0;
        selectedProfiles.forEach((profile: string) => {
            var found = false;
            selectedSubscriptionTypes.forEach((subscription: DataRow) => {
                if ((subscription.profile == profile)) {
                    found = true;
                }
            });
            if (!found && (profile != 'FINANCIAL_REPRESENTATIVE') && (profile != 'DRAWEE')) {
                toast.error(`É necessário selecionar pelo menos um tipo de assinatura para o perfil ${profilesMap[profile] || profile}.`);
                count_not_found++;
            }
        });
        return count_not_found === 0;
    }

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    const formatMobilePhone = () => {
        let phone = form.value.mobile;
        phone = phone.replace(/\D/g, '')
        if (form.value.isNationalNumber == "true") {
            phone = phone.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1)$2').replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1');
        }
        form.value = {
            ...form.value,
            mobile: phone,
        }
    }

    useEffect(() => {
        if (data && personId) {
            // 🧹 CORREÇÃO: Verificar se é acesso direto à edição
            const personUUID = getPersonUUID() || "";
            const storedProfiles = ProfileStorageManager.load('individual', personUUID);
            
            // Se há profiles no localStorage mas é acesso direto (não veio do modal)
            if (storedProfiles && storedProfiles.length > 0 && 
                (location.state?.fromDirectEdit || !location.state?.fromProfileModal)) {
                console.log('🧹 Acesso direto à edição - limpando localStorage órfão:', storedProfiles);
                ProfileStorageManager.clear('individual', personUUID);
            }
            
            loadProvinces();
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            let address = data.addresses.find((address: Address) => address.is_active);
            let contacts = data.contacts.find((tmp: any) => tmp.type == 'MOBILE' && tmp.is_active);

            if (!address) {
                address = {
                    ...address,
                    id: '',
                    person_id: '',
                    zipcode: '',
                    street: '',
                    street_number: '',
                    complement: '',
                    district: '',
                    city_id: '',
                    street_type: '',
                    is_active: false,
                };
            }

            form.value = {
                ...form.value,
                id: data.id,
                uuid: data.uuid,
                socialId: data.socialId,
                name: data.name,
                birthday: data.birthday,
                maritalStatus: data.maritalStatus ? data.maritalStatus.id : "",
                matrimonialRegimeId: data.matrimonialRegime ? data.matrimonialRegime.id : "",
                mobile: data.mobile,
                phone: data.phone.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1)$2').replace(/(\d{4})(\d)/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1'),
                email: data.email,
                gender: data.gender,
                isCountryman: data.isCountryman,
                PEP: data.PEP,
                activeAddress: address,
                addresses: data.addresses,
                profiles: data.profiles,
                contacts: data.contacts,
                idCard: data.idCard ? data.idCard.number : null,
                idCardType: data.idCard ? data.idCard.type : null,
                idCardProvince: data.idCard ? data.idCard.province : null,
                issuingAuthority: data.idCard ? data.idCard.issuingAuthority : null,
                isNationalNumber: contacts?.is_national_number == true ? "true" : "false",
                nationalityId: data.nationalityId,
                professionId: data.professionId,
                spouseSocialId: data.spouseSocialId || '',
                spouseName: data.spouseName || '',
                consentingParty: data.consentingParty,
                bankAccounts: data.bankAccounts || [],
            };


            if (address.city_id) {
                getCity(address.city_id).then(city => {
                    if (city) {
                        form.value = {
                            ...form.value,
                            activeAddress: {
                                ...form.value.activeAddress,
                                province_initials: city["province.initials"],
                            }
                        }

                        fetchCitiesWrapper(city["province.initials"]);
                    }
                });
            }
            addTab(getTabItem("tab-0"));
            addTab(getTabItem("tab-1"));
            addTab(getTabItem("tab-3")); // ✅ SEMPRE mostrar aba Anuentes
            
            // ✅ SEMPRE adicionar abas ALL (Endereços, Contatos, Documentos)
            console.log('🔍 DEBUG: Adicionando abas ALL...');
            console.log('🔍 DEBUG: profileTabList:', profileTabList);
            profileTabList.forEach(profileTab => {
                console.log('🔍 DEBUG: Verificando profileTab:', profileTab.name, profileTab.id);
                if (profileTab.name === 'ALL') {
                    console.log('✅ DEBUG: Adicionando aba ALL:', profileTab.id, profileTab.title);
                    addTab(getTabItem(profileTab.id));
                }
            });
            console.log('🔍 DEBUG: tabItems após adicionar ALL:', tabItems.value.map(t => t.id));
            
            //Carregar tabs cadastradas no banco de dados
            if (data.profiles) {
                data.profiles.forEach((profile: any) => {
                    profileTabList.forEach(profileTab => {
                        if (profileTab.name.includes(profile.type)) {
                            const tabItem = profileTab;
                            addTab(getTabItem(tabItem.id));
                        }
                    });
                });
            }
            //carregar novos tabs adicionados por um novo perfil
            if (profilesArray) {
                profilesArray.value.forEach((profile: any) => {
                    profileTabList.forEach(profileTab => {
                        if (profileTab.name.includes(profile)) {
                            const tabItem = profileTab;
                            addTab(getTabItem(tabItem.id));
                        }
                    });
                });
            }

            // ✅ Usar profiles do localStorage (loadedProfiles) ao invés da query string
            form.value = {
                ...form.value,
                profiles: loadedProfiles.length > 0 ? loadedProfiles : profiles.split(','),
            }
            banks.value = data.banks || [];
            individualUUID.value = data.uuid;
            isEditing.value = profiles == '' ? true : false;
            formatMobilePhone();
            loadProfilesSubscription();
            // ✅ CORREÇÃO: loadProfileList apenas se localStorage estiver vazio
            if (loadedProfiles.length === 0) {
                loadProfileList(isEditing.value);
            }
            checkFieldToBlockEditing(isEditing.value, data);
        }
    }, [data]);

    const loadProfileList = (isEditing: boolean) => {
        if (isEditing) {
            const bankProfiles: string[] = [];
            data.profiles.forEach((profile: any) => {
                bankProfiles.push(profile.type);
            });
            
            const personUUID = getPersonUUID() || "";
            const localStorageProfiles = ProfileStorageManager.load('individual', personUUID);
            
            console.log('🔍 DEBUG loadProfileList:');
            console.log('  - bankProfiles (já salvos):', bankProfiles);
            console.log('  - localStorageProfiles (recentes):', localStorageProfiles);
            
            // ✅ LÓGICA: localStorage tem prioridade (mais recente que banco)
            // Só usar banco se localStorage estiver vazio
            if (localStorageProfiles.length > 0) {
                console.log('✅ Usando localStorage (mais recente):', localStorageProfiles);
                profilesArray.value = localStorageProfiles;
            } else if (bankProfiles.length > 0) {
                console.log('✅ Usando banco (fallback):', bankProfiles);
                profilesArray.value = bankProfiles;
                saveProfiles(bankProfiles); // Sincronizar com localStorage
            } else {
                console.log('⚠️ Nenhum profile encontrado');
            }
        }
    }

    const checkFieldToBlockEditing = (isEditing: boolean, data: any) => {
        if (isEditing) {
            form.value.blockEdit = {
                blockIdCard: data.idCard.number != "",
                blockBirthday: data.birthday != "",
                blockNationality: data.nationalityId != null,
            };
        }

    }

    const setTabIndex = (tabIndex: number) => {
        navigate(`?tab=${tabIndex}&profiles=${profiles}`);
    };

    const profileSubscriptionContains = (values: DataRow[], sign_as: string) => {
        var found = false;
        values.forEach((item: DataRow) => {
            if (item.label == sign_as) {
                found = true;;
            }
        });
        return found;
    };

    const loadSpouseName = async (socialId: string) => {
        const token = localStorage.getItem('authToken');
        isLoadingForm.value = true;
        
        try {
            const response = await fetch(`${BASE_URL}` + "/" + socialId, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });
            
            let spouseName = "";
            if (!response.ok) {
                // Tentar ler mensagem de erro, mas não falhar se não conseguir
                try {
                    let message = await response.json();
                    console.log('API error response:', message);
                } catch (jsonError) {
                    console.log('Could not parse error response as JSON');
                }
                // REMOVIDO: toast.error("Cônjuge não encontrado...");
                spouseName = 'Ainda não cadastrado';
            } else {
                const data = await response.json();
                spouseName = data.name || 'Nome não disponível';
            }
            
            form.value = {
                ...form.value,
                spouseName: spouseName,
            };
        } catch (error) {
            console.error('Erro ao buscar cônjuge:', error);
            // REMOVIDO: toast.error("Cônjuge não encontrado...");
            form.value = {
                ...form.value,
                spouseName: 'Ainda não cadastrado',
            };
        } finally {
            isLoadingForm.value = false;
        }
        
        return true;
    }
    const changeForm = (field: string, value: string) => {
        let fieldName = '';
        let attribute = '';
        if (field.includes('.')) {
            fieldName = field.split('.')[0];
            attribute = field.split('.')[1];
        } else {
            fieldName = field;
        }
        if (fieldName == 'activeAddress') {
            form.value = {
                ...form.value,
                activeAddress: {
                    ...form.value.activeAddress,
                    [attribute]: value,
                }
            }
        } else {
            form.value = {
                ...form.value,
                [fieldName]: value,
            }
        }

        // Auto-seleção de regime matrimonial e limpeza de campos
        if (fieldName === 'maritalStatus') {
            let updates: any = {};
            
            if (value === '2' || value === '6') {
                // Casado ou união estável - auto-selecionar comunhão parcial
                updates.matrimonialRegimeId = '2';
            } else {
                // Outros estados - limpar campos relacionados
                updates.matrimonialRegimeId = '';
                updates.spouseSocialId = '';
                updates.spouseName = '';
            }
            
            form.value = { ...form.value, ...updates };
        }

        if (fieldName == 'spouseSocialId') {
            let spouseSocialId = form.value.spouseSocialId.replace(/\D/g, '');
            if (spouseSocialId.length == 11) {
                loadSpouseName(spouseSocialId);
            }
            if (spouseSocialId.length < 11) {
                form.value = {
                    ...form.value,
                    spouseName: 'Ainda não cadastrado',
                };
            }

        }
        if (fieldName == 'consentingParty') {
            form.value = {
                ...form.value,
                selectedConsentingParty: value,
            }
        }
        removeErrorField(errorFields.value, fieldName);
    };

    const generateProfileList = computed(() => {
        // 🚨 FIX: Evitar re-renderização se profiles estão vazios
        if (!profilesArray.value || profilesArray.value.length === 0) {
            return <div>Nenhum perfil associado</div>;
        }
        
        // 🚨 FIX: Log de profiles
        console.log('🔄 generateProfileList executado:', profilesArray.value);
        
        return (
            <div className="grid grid-cols-1 gap-8">
                <div className="flex space-x-1 col-span-1">
                    {
                        profilesArray.value
                            .filter((profile: string) => {
                                const hasMapping = profilesMap[profile];
                                console.log(`🔍 Profile ${profile} tem mapeamento: ${hasMapping}`);
                                return hasMapping;
                            })
                            .map((profile: string, idx: number) => (
                                <div className="flex items-center" key={profile + idx}>
                                    <span className="bg-gray-300 text-gray-600 px-2 py-1 rounded">{profilesMap[profile]}</span>
                                </div>
                            ))
                    }
                </div>
            </div>
        );
    });

    // Validação: personId é obrigatório
    if (!personId) {
        return <div>Erro: ID da pessoa não encontrado</div>;
    }

    return (
        <PageForm
            pageTitle={`Alterar pessoa #${form.value.id}`}
            apiVersion={apiVersion.value}
            isLoading={isLoading || isSendingForm.value || isLoadingForm.value}
            breadcrumb={[
                { label: "Listagem de PFs", url: "/person/individual/0/100/id/ASC/" },
                { label: `Alterar parceira #${form.value.id}`, url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button
                        id="btn-cancel-individual"
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        title="Cancelar registro"
                        disabled={isLoading || isSendingForm.value}
                    >
                        Cancelar
                    </button>

                    {
                        <button
                            id="btn-save-individual"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={submitForm}
                            title="Atualizar registro"
                            disabled={updateButtonDisable.value}

                        >
                            Atualizar
                        </button>
                    }
                </div>
            )}
        >
            <div>
                <div className="grid grid-cols-1 gap-8">
                    <div className="col-span-1">
                        <h2 className="text-lg font-semibold text-gray-800">
                            {form.value.name}
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-8">
                    <div className="flex space-x-1 col-span-1">
                        {generateProfileList.value}
                    </div>

                </div>
            </div>
            <Fragment>
                <Tab tabItems={tabItems.value} tabIndex={tabIndex} setTabIndex={setTabIndex}></Tab>
                <div id="tab-content" className="overflow-y-auto">

                    {tabIndex == 0 &&
                        <IndividualFormBasicData
                            key="tab1"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={isEditing.value}
                        />
                    }

                    {tabIndex == 1 &&
                        <IndividualFormAdditionalData
                            key="tab2"
                            form={form.value}
                            profiles={profilesArray.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            genders={genders.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={isEditing.value}
                        />
                    }

                    {tabIndex == 2 &&
                        <IndividualFormSubscription
                            key="tab3"
                            form={form.value}
                            subscriptionTypes={subscriptionTypes.value}
                            profilesSubscription={profilesSubscription.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            editForm={isEditing.value}
                        />
                    }

                    {tabIndex == 3 &&
                        <IndividualFormConsentingParty
                            key="tab4"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            editForm={isEditing.value}
                        />
                    }

                    {tabIndex == 4 &&
                        <IndividualFormAddresses
                            individualId={personId}
                        />
                    }

                    {tabIndex == 5 &&
                        <IndividualFormContacts
                            individualId={personId}
                            contacts={form.value.contacts || []}
                            onContactsChange={(contacts) => {
                                form.value = { ...form.value, contacts };
                            }}
                        />
                    }

                    {tabIndex == 6 &&
                        <IndividualFormDocuments
                            individualId={personId}
                            documents={form.value.documents || []}
                            onDocumentsChange={(documents) => {
                                form.value = { ...form.value, documents };
                            }}
                        />
                    }

                    {tabIndex == 7 &&
                        <IndividualFormBankAccount
                            individualId={individualUUID.value}
                            provinces={provinces.value}
                            banks={banks.value}
                            bankAccounts={form.value.bankAccounts}
                        />
                    }

                </div>
            </Fragment>
            <span className="text-gray-500 text-xs italic px-2 py-1">*-Campos obrigatórios</span>

        </PageForm >
    );
};


export default IndividualPageEdit;